import React, { useState } from 'react';
import { Button } from './index';
import { saveMentorAvailability, AvailabilitySlot } from '../services/mentor-availability-service';

const DAYS: { key: AvailabilitySlot['day']; label: string }[] = [
  { key: 'monday',    label: 'Monday' },
  { key: 'tuesday',   label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday',  label: 'Thursday' },
  { key: 'friday',    label: 'Friday' },
  { key: 'saturday',  label: 'Saturday' },
  { key: 'sunday',    label: 'Sunday' },
];

function timeOptions(): string[] {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      if (h === 23 && m === 30) continue; // max start = 23:00 so endTime = 24:00... limit to 22:30
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  // remove 23:30 — start times only valid up to 22:30 so +1hr ≤ 23:30
  return opts.filter(t => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m <= 22 * 60 + 30;
  });
}

const TIME_OPTS = timeOptions();

function addOneHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + 60;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

interface DayState {
  enabled: boolean;
  slots: string[]; // array of startTime strings; endTime = startTime + 1hr
}

type DayMap = Record<AvailabilitySlot['day'], DayState>;

const defaultDay = (): DayState => ({ enabled: false, slots: [] });

const initialMap = (): DayMap => ({
  monday: defaultDay(), tuesday: defaultDay(), wednesday: defaultDay(),
  thursday: defaultDay(), friday: defaultDay(), saturday: defaultDay(), sunday: defaultDay(),
});

const fromSlots = (slots: AvailabilitySlot[]): DayMap => {
  const m = initialMap();
  slots.forEach(s => {
    m[s.day].enabled = true;
    if (!m[s.day].slots.includes(s.startTime)) m[s.day].slots.push(s.startTime);
  });
  return m;
};

interface Props {
  onSaved: () => void;
  onClose?: () => void;
  initialSlots?: AvailabilitySlot[];
}

const MentorAvailabilityModal: React.FC<Props> = ({ onSaved, onClose, initialSlots }) => {
  const [days, setDays] = useState<DayMap>(() => initialSlots?.length ? fromSlots(initialSlots) : initialMap());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const enabledDays = DAYS.filter(d => days[d.key].enabled && days[d.key].slots.length > 0);
  const totalHours  = enabledDays.reduce((sum, d) => sum + days[d.key].slots.length, 0);
  const valid = enabledDays.length >= 2 && totalHours >= 5;

  const toggle = (day: AvailabilitySlot['day']) => {
    setDays(prev => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }));
  };

  const addSlot = (day: AvailabilitySlot['day'], startTime: string) => {
    setDays(prev => ({
      ...prev,
      [day]: { ...prev[day], slots: [...prev[day].slots, startTime].sort() },
    }));
  };

  const removeSlot = (day: AvailabilitySlot['day'], startTime: string) => {
    setDays(prev => ({
      ...prev,
      [day]: { ...prev[day], slots: prev[day].slots.filter(t => t !== startTime) },
    }));
  };

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    try {
      const slots: AvailabilitySlot[] = enabledDays.flatMap(d =>
        days[d.key].slots.map(startTime => ({
          day: d.key,
          startTime,
          endTime: addOneHour(startTime),
        }))
      );
      await saveMentorAvailability(slots);
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-shell border border-line rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-xl font-bold text-text">Set Your Weekly Availability</h2>
          {onClose && (
            <button onClick={onClose} className="text-muted hover:text-text transition-colors flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="text-muted text-sm mb-6">
          Add 1-hour slots for each day you're available. Select at least 2 days and 5 total hours.
        </p>

        <div className="space-y-3 mb-6">
          {DAYS.map(({ key, label }) => {
            const s = days[key];
            const usedTimes = new Set(s.slots);
            const availableTimes = TIME_OPTS.filter(t => !usedTimes.has(t));

            return (
              <div key={key} className={`border rounded-xl p-4 transition-colors ${s.enabled ? 'border-chart bg-chart/5' : 'border-line'}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={`day-${key}`}
                    checked={s.enabled}
                    onChange={() => toggle(key)}
                    className="w-4 h-4 accent-chart"
                  />
                  <label htmlFor={`day-${key}`} className="text-text font-medium text-sm cursor-pointer flex-1">
                    {label}
                  </label>
                  {s.enabled && (
                    <span className="text-xs text-muted">{s.slots.length} slot{s.slots.length !== 1 ? 's' : ''}</span>
                  )}
                </div>

                {s.enabled && (
                  <div className="mt-3 pl-7 space-y-2">
                    {/* Existing slot pills */}
                    {s.slots.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {s.slots.map(startTime => (
                          <span
                            key={startTime}
                            className="inline-flex items-center gap-1.5 bg-chart/10 text-chart text-xs px-2.5 py-1 rounded-lg border border-chart/20"
                          >
                            {startTime}–{addOneHour(startTime)}
                            <button
                              onClick={() => removeSlot(key, startTime)}
                              className="text-chart/60 hover:text-chart transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Warning if day enabled but no slots */}
                    {s.slots.length === 0 && (
                      <p className="text-xs text-warning">Add at least one slot for this day.</p>
                    )}

                    {/* Add slot picker */}
                    {availableTimes.length > 0 && (
                      <select
                        value=""
                        onChange={e => { if (e.target.value) addSlot(key, e.target.value); }}
                        className="bg-surface border border-line rounded-lg px-3 py-1.5 text-text text-sm w-full cursor-pointer"
                      >
                        <option value="">+ Add slot</option>
                        {availableTimes.map(t => (
                          <option key={t} value={t}>{t} – {addOneHour(t)}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={`text-sm mb-4 p-3 rounded-lg ${valid ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
          {enabledDays.length} day{enabledDays.length !== 1 ? 's' : ''} · {totalHours} slot{totalHours !== 1 ? 's' : ''} ({totalHours} hour{totalHours !== 1 ? 's' : ''})
          {!valid && (
            <span className="ml-2">
              {enabledDays.length < 2 ? '— need at least 2 days with slots' : '— need at least 5 hours total'}
            </span>
          )}
        </div>

        {error && <p className="text-danger text-sm mb-4">{error}</p>}

        <Button variant="primary" onClick={handleSave} disabled={!valid || saving} className="w-full">
          {saving ? 'Saving…' : 'Save Availability'}
        </Button>
      </div>
    </div>
  );
};

export default MentorAvailabilityModal;
